import * as React from 'react';
import Image from 'next/image';
import { useAtomValue } from 'jotai';
import { ArrowLongRightIcon, BuildingOfficeIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';

import { restaurantDetailsAtom } from '.';
import { customImageLoader, imageVariant, scrollToView } from '@restworld/utils-common';
import { Button as ButtonV2 } from '@restworld/ui-ds-base';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IVenueInformationProps {}

const VenueLogo: React.FC<{ logo: string }> = ({ logo }) => (
  <div className="self-stretch inline-flex justify-start items-center gap-2">
    <div className="w-11 h-11 relative rounded-lg outline outline-1 outline-offset-[-1px] outline-neutral-90 overflow-hidden">
      <Image
        loader={customImageLoader}
        fill
        alt="logo"
        className="object-cover object-center"
        src={imageVariant(logo, 'sm') ?? logo}
        priority
      />
    </div>
  </div>
);

const VenuePictures: React.FC<{ pictures: string[] }> = ({ pictures }) => {
  return (
    <div className="w-full">
      {pictures.length > 0 && (
        <div className="relative w-full">
          <ul className="flex overflow-x-auto gap-2 snap-x snap-mandatory snap-always scrollbar-none lg:scrollbar-thin venue-pictures">
            {pictures.map((picture, index) => (
              <li
                key={index}
                className="relative w-32 h-24 lg:w-[11rem] lg:h-[8rem] shrink-0 snap-start snap-always snap-x snap-mandatory"
              >
                <Image
                  loader={customImageLoader}
                  fill
                  alt={`venue-picture-${index}`}
                  className="object-cover object-center rounded-lg"
                  src={imageVariant(picture, 'md') ?? picture}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const VenueLocationCity: React.FC<{ city: string }> = ({ city }) => (
  <div className="inline-flex justify-center items-center gap-1">
    <BuildingOfficeIcon className="w-4 h-4 relative" />
    <h5 className="text-neutral-700 text-sm font-medium">{city}</h5>
  </div>
);

const VenueLocationDesc: React.FC<{ desc: string }> = ({ desc }) => (
  <div className="inline-flex justify-center items-center gap-1 w-full">
    <MapPinIcon className="w-4 h-4 relative max-w-4 min-w-4" />
    <h5 className="text-neutral-700 text-sm font-medium w-full">{desc}</h5>
  </div>
);

const DirectionsButton: React.FC<{ googleMapsURL: string; label: string }> = ({ googleMapsURL, label }) => {
  const handleDirectionsClick = React.useCallback(() => {
    if (googleMapsURL) window.open(googleMapsURL, '_blank');
  }, [googleMapsURL]);
  return (
    <ButtonV2 onClick={handleDirectionsClick} variant="secondary-neutral" size="sm" EndIcon={ArrowLongRightIcon}>
      {label}
    </ButtonV2>
  );
};

const JobPositionsButton: React.FC<{ label: string }> = ({ label }) => (
  <ButtonV2
    variant="secondary-restworld"
    size="sm"
    onClick={() => {
      scrollToView('job-positions', {
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    }}
  >
    {label}
  </ButtonV2>
);

export const VenueInformation: React.FC<IVenueInformationProps> = () => {
  const restaurantDetails = useAtomValue(restaurantDetailsAtom);
  const { t } = useTranslation('restaurant-showcase-v2');
  const {
    restaurantLogo,
    pictures = [],
    restaurantName,
    mapLocation,
    activeJobPositions = [],
  } = restaurantDetails || {};

  const { locality: restaurantLocationCity, address: restaurantLocationDesc, googleMapsURL } = mapLocation || {};

  return (
    <div className="self-stretch inline-flex flex-col justify-start items-start gap-4">
      {!!restaurantLogo && <VenueLogo logo={restaurantLogo} />}
      {/* Single H1 per page = restaurant name, prefixed by the "Lavora con noi" */}
      {/* eyebrow. Visual hierarchy unchanged; only the semantic tag changed. */}
      <h1 className="m-0 text-2xl lg:text-[32px] font-semibold leading-tight">
        <span className="block text-neutral-200">{t('work_with')}</span>
        <span className="block text-neutral-700">{restaurantName}</span>
      </h1>
      <VenuePictures pictures={pictures} />
      {!!restaurantLocationCity && <VenueLocationCity city={restaurantLocationCity} />}
      {!!restaurantLocationDesc && <VenueLocationDesc desc={restaurantLocationDesc} />}
      {!!googleMapsURL && <DirectionsButton googleMapsURL={googleMapsURL} label={t('directions')} />}
      {activeJobPositions?.length > 0 && (
        <JobPositionsButton
          label={t('open_job_position', {
            count: activeJobPositions.map((job) => job.jobs.length).reduce((a, b) => a + b, 0),
          })}
        />
      )}
    </div>
  );
};

export default VenueInformation;
